// components/public/WaitlistForm.tsx
/**
 * @description Waitlist signup form for fully leased buildings
 * Collects email and optional info to notify when units become available
 */
import { useState } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { joinWaitlist } from '../../lib/api';

interface WaitlistFormProps {
  buildingId: string;
  buildingName: string;
}

export function WaitlistForm({ buildingId, buildingName }: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubmitting(true);
    setError(null);

    const { error: submitError } = await joinWaitlist({
      building_id: buildingId,
      email,
      name: name || undefined,
      phone: phone || undefined,
    });

    setSubmitting(false);

    if (submitError) {
      setError(submitError);
    } else {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 rounded-xl p-6 text-center">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Check size={24} className="text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          You're on the list!
        </h3>
        <p className="text-sm text-gray-600">
          We'll notify you at <strong>{email}</strong> when a unit becomes available at {buildingName}.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
          <Bell size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Fully Leased</h3>
          <p className="text-sm text-gray-500">Join the waitlist to be notified</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address *"
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional)"
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <button
          type="submit"
          disabled={!email || submitting}
          className="w-full py-3 bg-primary hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Joining...
            </>
          ) : (
            'Notify Me When Available'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          We'll only email you when units become available. No spam.
        </p>
      </form>
    </div>
  );
}
